import { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import "./AddressFieldset.scss";
import { FormikTextField } from "app/components/form/FormikTextField/FormikTextField";
import { FormikSelectField } from "app/components/form/FormikSelectField/FormikSelectField";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import AddressAutocompleteFormik from "app/pages/.shared/form/AddressAutocompleteFormik";
import { useCountries } from "app/utils/intl/useCountries";
import { useFormikContext } from "formik";
import { useStateAndProvinces } from "app/utils/intl/useStateAndProvince";
import { COUNTRY_WITH_STATE_OR_PROVINCES } from "app/constants";
import classNames from "classnames";

const FIELD_NAMES = {
	ADDRESS_LINE1: "address-line1",
	ADDRESS_LINE2: "address-line2",
	POSTAL_CODE: "postal-code",
	ADDRESS_LEVEL2: "address-level2",
	COUNTRY_NAME: "country-name",
	STATE: "state",
};

const AddressFieldset = ({
	isRequired = false,
	setFieldValue,
	blocPrefixName = "",
	setFieldError,
}) => {
	const { track } = useAnalytics();

	const [loadingAddressDetail, setLoadingAddressDetail] = useState(false);

	const { values } = useFormikContext();

	const countryFieldValue = values?.contact?.["country-name"];
	const showStateField = COUNTRY_WITH_STATE_OR_PROVINCES.includes(countryFieldValue);

	const countries = useCountries();

	const stateAndProvinces = useStateAndProvinces(countryFieldValue);

	const fieldNames = useMemo(() => {
		const prefix = blocPrefixName ? `${blocPrefixName.toLowerCase()}.` : "";
		return {
			addressLine1Name: `${prefix}${FIELD_NAMES.ADDRESS_LINE1}`,
			addressLine2Name: `${prefix}${FIELD_NAMES.ADDRESS_LINE2}`,
			postalCodeName: `${prefix}${FIELD_NAMES.POSTAL_CODE}`,
			addressLevel2Name: `${prefix}${FIELD_NAMES.ADDRESS_LEVEL2}`,
			countryName: `${prefix}${FIELD_NAMES.COUNTRY_NAME}`,
			state: `${prefix}${FIELD_NAMES.STATE}`,
		};
	}, [blocPrefixName]);

	const handleBeforeAddressSelection = useCallback(() => {
		setLoadingAddressDetail(true);
	}, []);

	const handleAddressSelection = useCallback(
		place => {
			setLoadingAddressDetail(false);

			const countryCode = countries.find(
				country => country.name === place.components.country
			);
			// Formik stores address fields either at the root or under the bloc prefix (e.g. contact.*)
			const formSectionKey = blocPrefixName ? blocPrefixName.toLowerCase() : null;
			const formSectionValues = formSectionKey ? values?.[formSectionKey] : values;
			const previousPostalCode = formSectionValues?.[FIELD_NAMES.POSTAL_CODE];
			const previousState = formSectionValues?.[FIELD_NAMES.STATE];

			if (countryCode) {
				setFieldValue(fieldNames.countryName, countryCode.cca2);
				setFieldError(fieldNames.countryName);
			}

			setFieldValue(fieldNames.addressLine1Name, place.name);
			setFieldError(fieldNames.addressLine1Name);
			setFieldValue(fieldNames.addressLine2Name, undefined);
			setFieldError(fieldNames.addressLine2Name);
			const nextPostalCode = place?.components?.postalCode ?? previousPostalCode ?? "";
			setFieldValue(fieldNames.postalCodeName, nextPostalCode);
			setFieldError(fieldNames.postalCodeName);
			setFieldValue(fieldNames.addressLevel2Name, place.components.city);
			setFieldError(fieldNames.addressLevel2Name);
			const nextState = place?.components?.state ?? previousState ?? "";
			setFieldValue(fieldNames.state, nextState);
			setFieldError(fieldNames.state);

			track("google_autocomplete_address_selected", { address: place.formattedAddress });
		},
		[blocPrefixName, countries, fieldNames, setFieldError, setFieldValue, values]
	);

	const handleAutocompleteError = useCallback((type, status) => {
		track("google_autocomplete_error", { type, status });
	}, []);

	const gridClass = classNames("address__grid", {
		"address__grid--state": showStateField,
	});

	return (
		<div className="address">
			<div className="address__line1 address__input address__item">
				<AddressAutocompleteFormik
					isRequired={isRequired}
					id="address-line1"
					name={fieldNames.addressLine1Name}
					onError={handleAutocompleteError}
					onPlaceSelect={handleAddressSelection}
					onBeforePlaceSelect={handleBeforeAddressSelection}
					label={<FormattedMessage id="address.address1.label" />}
					setFieldValue={setFieldValue}
					setFieldError={setFieldError}
					data-testid="address-line1-input"
					blocPrefixName={blocPrefixName}
				/>
			</div>

			<div className="address__line2 address__input address__item">
				<FormikTextField
					id="address-line2"
					name={fieldNames.addressLine2Name}
					label={<FormattedMessage id="address.address2.label" />}
					data-testid="address-line2-input"
				/>
			</div>

			<div className={gridClass}>
				<div className="address__postcode address__input address__item">
					<FormikTextField
						id="postal-code"
						name={fieldNames.postalCodeName}
						isRequired={isRequired}
						loading={loadingAddressDetail}
						label={<FormattedMessage id="address.postCode.label" />}
						maxLength={12}
						data-testid="postal-code-input"
					/>
				</div>
				<div className="address__city address__input address__item">
					<FormikTextField
						id="address-level2"
						name={fieldNames.addressLevel2Name}
						isRequired={isRequired}
						loading={loadingAddressDetail}
						label={<FormattedMessage id="address.city.label" />}
						maxLength={50}
						data-testid="city-input"
					/>
				</div>
				{showStateField && (
					<div className="address__input address__item">
						<FormikSelectField
							id="state"
							name={fieldNames.state}
							isRequired={isRequired}
							loading={loadingAddressDetail}
							label={<FormattedMessage id="address.state.label" />}
							data-testid="state-select-input"
						>
							<option />
							{stateAndProvinces.map(province => (
								<option key={province.code} value={province.code}>
									{province.name}
								</option>
							))}
						</FormikSelectField>
					</div>
				)}
				<div className="address__country address__select address__item">
					<FormikSelectField
						id="country-name"
						name={fieldNames.countryName}
						isRequired={isRequired}
						loading={loadingAddressDetail}
						label={<FormattedMessage id="address.country.label" />}
						data-testid="country-select-input"
					>
						<option />
						{countries.map(country => (
							<option key={country.cca2} value={country.cca2}>
								{country.name}
							</option>
						))}
					</FormikSelectField>
				</div>
			</div>
		</div>
	);
};

AddressFieldset.propTypes = {
	isRequired: PropTypes.bool,
	setFieldValue: PropTypes.func.isRequired,
	blocPrefixName: PropTypes.string,
	setFieldError: PropTypes.func.isRequired,
};

export default memo(AddressFieldset);
